/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';
import { ConvaiClient } from 'convai-web-sdk';
import { useAnamConvaiAudioQueue } from './useAnamConvaiAudioQueue';
import { useSimulationContext } from '../providers/SimulationProvider';
import {
  SIMULATION_TYPES,
  MESSAGE_FROM,
  MESSAGE_TYPES,
  NUDGYT_AGENT_MESSAGE_TYPES,
  VOICE_MODE_STATUS,
  VAD_STATES
} from '../constants/simulationConstants';
import {
  sanitizeMessage,
  postMessage,
  getSelectedPersonalityId,
  containsMessageNotSent,
  createNarrativeTemplateKeysMap,
  isIOS
} from '../helpers/utils';
import convaiConfig from '../config/convaiConfig';
import useResponseTimeout from './useResponseTimeout';
import useVoiceActivityDetection from './useVoiceActivityDetection';
import useRequestUserEmotionInterval from './useRequestUserEmotionInterval';
import useRequestedUserEmotionListener from './useRequestedUserEmotionListener';

function useAnamConvaiClient({
  aiResponsePlaybackActiveRef,
  isVideoStreamReady,
  handleSendAudioChunk,
  handleEndAudioSequence
}) {
  const isInitialized = useRef(false);
  const userEmotionRef = useRef(null);
  const previousCharacterIdRef = useRef(null);
  const convaiClient = useRef(null);
  const aiTranscriptRef = useRef('');
  const isResponseCancelledRef = useRef(false);
  const clearResponseTimeoutRef = useRef(null);
  const handleStopResponseRef = useRef(null);
  const handleStopAudioRef = useRef(null);
  const [isAiResponseInFlight, setIsAiResponseInFlight] = useState(false);
  const prevIsAiTalkingForEmotionRef = useRef(false);

  const { stageChunk, commitStagedChunks, discardStagedChunks, flushQueue } =
    useAnamConvaiAudioQueue({
      handleSendAudioChunk,
      handleEndAudioSequence
    });

  const {
    userText,
    isAiTalking,
    isDebugVad,
    userEmotion,
    characterIds,
    simulationType,
    convaiSessionId,
    isVoiceModeActive,
    isClientInitialized,
    selectedCharacterId,
    previousLevelContext,
    isEmotionRecognitionEnabled,
    setUserText,
    setIsAiTalking,
    setConvaiSessionId,
    setIsClientInitialized,
    setIsVoiceModeActive,
    setIsUserInterrupt
  } = useSimulationContext();

  const personalityId = getSelectedPersonalityId(
    characterIds,
    selectedCharacterId
  );

  useRequestedUserEmotionListener(convaiClient, isEmotionRecognitionEnabled);

  /**
   * Drops everything still queued for the character's voice. Clearing the
   * talking flag alone left staged chunks to be committed afterwards, so the
   * character kept speaking through an interruption.
   */
  const clearAudioQueue = () => {
    setIsAiTalking(false);
    discardStagedChunks();
    flushQueue();
  };

  const {
    startAIListening,
    postVADState,
    isMicMutedRef,
    cancelDebouncedAudioChunk
  } = useVoiceActivityDetection({
    convaiClient,
    clearAudioQueue,
    clearResponseTimeout: () => clearResponseTimeoutRef.current?.()
  });

  /**
   * Response timeout handler
   * Detects when Convai AI fails to respond within 30 seconds
   */
  const { startResponseTimeout, clearResponseTimeout } = useResponseTimeout({
    onTimeout: () => {
      resetClientState();
      setIsAiResponseInFlight(false);
      if (isVoiceModeActive) {
        postVADState(VAD_STATES.IDLE);
        startAIListening();
      }
    }
  });

  /**
   * Emotion interval hook
   * Manages automatic user emotion broadcasting every 10 seconds
   */
  useRequestUserEmotionInterval({
    convaiClient,
    isVoiceModeActive,
    isClientInitialized: isInitialized.current,
    isAiTalking,
    isAiResponseInFlight
  });

  const resetClientState = () => {
    clearResponseTimeout();
    setIsAiTalking(false);
    discardStagedChunks();
    flushQueue();
  };

  // Stop AI response when parent sends STOP_RESPONSE
  const handleStopResponse = () => {
    isResponseCancelledRef.current = true;
    resetClientState();
    setIsAiResponseInFlight(false);
    aiTranscriptRef.current = '';
    aiResponsePlaybackActiveRef.current = false;

    convaiClient.current?.stopCharacterAudio?.();
    // Audio already handed to Anam is buffered inside its stream and keeps
    // playing unless the persona is interrupted — discarding our own queue is
    // not enough. AnamVideoStream watches this flag.
    setIsUserInterrupt(true);

    postMessage({
      type: MESSAGE_TYPES.CHAT,
      payload: { type: MESSAGE_TYPES.RESPONSE_ENDED }
    });
  };

  // Silence the character without cancelling the reply itself — the parent
  // sends this when it has decided the response must not be heard.
  const handleStopAudio = () => {
    resetClientState();
    setIsAiResponseInFlight(false);
    aiResponsePlaybackActiveRef.current = false;
    convaiClient.current?.stopCharacterAudio?.();
    setIsUserInterrupt(true);
  };

  handleStopResponseRef.current = handleStopResponse;
  handleStopAudioRef.current = handleStopAudio;

  const cleanupConvaiClient = () => {
    if (convaiClient.current) {
      try {
        // Try to disconnect/close the existing client
        if (typeof convaiClient.current.disconnect === 'function') {
          convaiClient.current.disconnect();
        }
        if (typeof convaiClient.current.close === 'function') {
          convaiClient.current.close();
        }
      } catch (error) {
        console.error('[Convai Client] Error cleaning up old client:', error);
      }
      convaiClient.current = null;
    }
  };

  const postAiResponse = (aiText) => {
    if (isResponseCancelledRef.current || !aiText) return;

    const avatarId = selectedCharacterId && selectedCharacterId?.split('_')[0];
    const characterId =
      convaiClient.current && avatarId
        ? avatarId.concat('_', convaiClient.current.characterId)
        : selectedCharacterId;

    const convaiSessionId = convaiClient.current?.sessionId ?? '';
    setConvaiSessionId(convaiSessionId);

    const payload = {
      from: MESSAGE_FROM.AI,
      characterId,
      message: sanitizeMessage(aiText),
      convaiSessionId
    };
    postMessage({ type: MESSAGE_TYPES.CHAT, payload });
    aiTranscriptRef.current = ''; // Reset for next response

    postMessage({
      type: MESSAGE_TYPES.CHAT,
      payload: { type: MESSAGE_TYPES.RESPONSE_ENDED }
    });
  };

  // Convai posts RESPONSE_ENDED before audio finishes; clear after playback ends.
  useEffect(() => {
    const wasAiTalking = prevIsAiTalkingForEmotionRef.current;
    prevIsAiTalkingForEmotionRef.current = isAiTalking;

    if (wasAiTalking && !isAiTalking && isAiResponseInFlight) {
      setIsAiResponseInFlight(false);
    }
  }, [isAiTalking, isAiResponseInFlight]);

  const convaiResponseCallback = async (response) => {
    if (!isInitialized.current) return;
    const hasAudioResponse = response.hasAudioResponse();

    if (response.hasUserQuery()) {
      const userQuery = response.getUserQuery();
      const transcript = userQuery.getTextData();
      const isFinal = userQuery.getIsFinal();
      if (!aiResponsePlaybackActiveRef.current)
        aiResponsePlaybackActiveRef.current = true;

      if (transcript && isFinal) vadFinalTranscript(transcript);
    }

    if (hasAudioResponse) {
      if (isResponseCancelledRef.current) return;

      clearResponseTimeout();

      if (!aiResponsePlaybackActiveRef.current)
        aiResponsePlaybackActiveRef.current = true;

      const getAudioResponse = response.getAudioResponse();

      const aiText = getAudioResponse.getTextData();

      // Accumulate AI text chunks
      if (aiText) aiTranscriptRef.current += aiText;

      const audioChunk = getAudioResponse.getAudioData_asB64();
      if (audioChunk && audioChunk.length > 0) {
        stageChunk(audioChunk);
      }

      // Handle end of AI response - assemble and enqueue
      if (getAudioResponse.getEndOfResponse()) await endOfAiResponse();
    }
  };

  const vadFinalTranscript = (transcript) => {
    if (isDebugVad) console.log('[VAD User Transcript]:', transcript);
    isResponseCancelledRef.current = false;
    isMicMutedRef.current = true;
    setIsAiResponseInFlight(true);
    startResponseTimeout();

    // Always get the latest userEmotion value from the ref
    const currentUserEmotion = userEmotionRef.current;

    postMessage({
      type: MESSAGE_TYPES.CHAT,
      payload: {
        from: MESSAGE_FROM.USER,
        message: transcript,
        ...(currentUserEmotion?.trim() && {
          userEmotion: currentUserEmotion?.toLowerCase()
        })
      }
    });
  };

  const endOfAiResponse = async () => {
    // Discard response if stopped or transcript marked not sent
    if (
      isResponseCancelledRef.current ||
      containsMessageNotSent(aiTranscriptRef.current)
    ) {
      discardStagedChunks();
      aiTranscriptRef.current = '';
      setIsAiResponseInFlight(false);
      return;
    }

    postAiResponse(aiTranscriptRef.current);

    commitStagedChunks();

    aiTranscriptRef.current = '';
  };

  const convaiErrorCallback = (type, message) => {
    console.error('[Convai Error]:', type, message);
    clearResponseTimeout();
    setIsAiResponseInFlight(false);

    // Reinitialize client if error is "STT streaming timeout"
    if (message && message.includes('STT streaming timeout')) {
      console.error(
        '[Convai Client] Reinitializing due to STT streaming timeout'
      );
      convaiClient.current.resetSession();
      postMessage({
        type: MESSAGE_TYPES.VOICE_MODE,
        error: message,
        payload: {
          status: VOICE_MODE_STATUS.INACTIVE
        }
      });

      isInitialized.current = false;
      cancelDebouncedAudioChunk();
      setIsVoiceModeActive(false);
      discardStagedChunks();
      flushQueue();
      initializeConvaiClient();
    } else {
      postMessage({ type: MESSAGE_TYPES.CHAT, error: message });
    }
  };

  const initializeConvaiClient = () => {
    if (!personalityId || simulationType !== SIMULATION_TYPES.BASIC) {
      return;
    }

    cleanupConvaiClient();
    resetClientState();

    console.log('[Convai Client] Initializing Convai client');

    const narrativeTemplateKeysMap =
      createNarrativeTemplateKeysMap(previousLevelContext);

    convaiClient.current = new ConvaiClient({
      apiKey: convaiConfig.apiKey,
      characterId: personalityId,
      enableAudio: false,
      faceModel: 3,
      enableFacialData: true,
      enableEmotionalData: true,
      ...(convaiSessionId && { sessionId: convaiSessionId }),
      ...(narrativeTemplateKeysMap.size > 0 && { narrativeTemplateKeysMap })
    });

    isInitialized.current = true;

    /**
     * Convai error callback
     * Handles SDK errors and clears response timeout
     * Reinitializes client if error is "STT streaming timeout"
     */
    convaiClient.current.setErrorCallback(convaiErrorCallback);

    /**
     * Convai response callback
     * Processes streaming audio, viseme, and emotion data from AI
     */
    convaiClient.current.setResponseCallback(convaiResponseCallback);
  };

  // Prevents stale closure in vadFinalTranscript()
  // Ensures it always reads the current emotion value
  useEffect(() => {
    userEmotionRef.current = userEmotion;
  }, [userEmotion]);

  //Parent: drain audio queue when AI transcript persist fails.
  useEffect(() => {
    const onParentMessage = (event) => {
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (data?.type !== MESSAGE_TYPES.CHAT) return;

      const payloadType = data?.payload?.type;
      if (payloadType === NUDGYT_AGENT_MESSAGE_TYPES.STOP_RESPONSE) {
        handleStopResponseRef.current?.();
        return;
      }
      if (payloadType === NUDGYT_AGENT_MESSAGE_TYPES.STOP_AUDIO) {
        handleStopAudioRef.current?.();
      }
    };

    window.addEventListener('message', onParentMessage);
    return () => window.removeEventListener('message', onParentMessage);
  }, []);

  // Update the ref whenever clearResponseTimeout changes
  useEffect(() => {
    clearResponseTimeoutRef.current = clearResponseTimeout;
  }, [clearResponseTimeout]);

  useEffect(() => {
    if (
      !isClientInitialized &&
      simulationType === SIMULATION_TYPES.BASIC &&
      isVideoStreamReady
    ) {
      setIsClientInitialized(true);

      // If the client is initialized on iOS, we don't need to send the INITIALIZED message
      // To prevent user to send message before tapping on the screen
      if (isIOS()) return;

      postMessage({ type: MESSAGE_TYPES.INITIALIZED });
    }
  }, [isClientInitialized, simulationType, isVideoStreamReady]);

  useEffect(() => {
    if (
      !personalityId ||
      simulationType !== SIMULATION_TYPES.BASIC ||
      isInitialized.current
    )
      return;
    if (!isInitialized.current) initializeConvaiClient();
  }, [
    userEmotion,
    personalityId,
    simulationType,
    convaiSessionId,
    isVoiceModeActive,
    isClientInitialized,
    selectedCharacterId,
    previousLevelContext
  ]);

  useEffect(() => {
    if (
      simulationType !== SIMULATION_TYPES.BASIC ||
      isVoiceModeActive ||
      !convaiClient
    )
      return;

    // Send user's message to the Convai client
    if (userText) {
      isResponseCancelledRef.current = false;
      // Reset client state (stop talking, clear facial data, etc.)
      resetClientState();

      // Discarding our staged chunks does not reach the speech Anam has
      // already buffered — without this the previous reply talks over the new
      // one.
      convaiClient.current?.stopCharacterAudio?.();
      setIsUserInterrupt(true);
      aiResponsePlaybackActiveRef.current = false;

      setIsAiResponseInFlight(true);

      if (
        previousCharacterIdRef.current &&
        previousCharacterIdRef.current !== personalityId
      ) {
        previousCharacterIdRef.current = personalityId;
        convaiClient.current.sendTextChunk('');
        return;
      }

      previousCharacterIdRef.current = personalityId;

      // Send text to Convai and start timeout timer
      convaiClient.current.sendTextChunk(userText);

      startResponseTimeout();

      // Note: Convai does not have text receive functionality for text chat, so we send back
      // the received text message to the parent application as a confirmation
      // that the message was successfully received and processed.
      const payload = {
        from: MESSAGE_FROM.USER,
        message: userText
      };
      postMessage({ type: MESSAGE_TYPES.CHAT, payload });

      setUserText('');
    }
  }, [
    userText,
    convaiClient,
    personalityId,
    simulationType,
    isVoiceModeActive
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return resetClientState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default useAnamConvaiClient;
